import { useState, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { databaseKeys } from '@/hooks/useDatabase'
import {
  HashtagIcon,
  TableCellsIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface IndexDetailsViewProps {
  connection: DatabaseConnection
  database: string
  indexName: string
  schema?: string
  onIndexDeleted?: () => void
}


// Helper function to parse table name from index definition
function parseTableNameFromDefinition(definition: string): string | null {
  if (!definition) return null
  
  // Match patterns like "ON table_name" or "ON schema.table_name"
  const onTableMatch = definition.match(/\bON\s+(?:[\w"]+\.)?[\w"]+/i)
  if (onTableMatch) {
    const tablePart = onTableMatch[0].replace(/^ON\s+/i, '').trim()
    // Remove schema prefix if present and quotes
    const tableName = tablePart.split('.').pop()?.replace(/"/g, '') || tablePart.replace(/"/g, '')
    return tableName
  }
  
  return null
}

interface IndexColumnWithOrder {
  name: string
  order: 'ASC' | 'DESC'
}

// Helper function to parse columns from index definition
function parseColumnsFromDefinition(definition: string): IndexColumnWithOrder[] {
  if (!definition) return []
  
  // Match patterns like "USING btree (column1, column2)" or "(column1, column2, column3)"
  const columnsMatch = definition.match(/\(([^)]+)\)/)
  if (columnsMatch) {
    const [, columnsPart] = columnsMatch
    // Split by comma and clean up each column name with ordering
    return columnsPart
      .split(',')
      .map(col => {
        const trimmed = col.trim().replace(/"/g, '') // Remove quotes
        const parts = trimmed.split(/\s+/) // Split by whitespace
        const [columnName] = parts
        
        // Look for ASC/DESC in the remaining parts
        const orderPart = parts.slice(1).find(part => 
          part.toUpperCase() === 'ASC' || part.toUpperCase() === 'DESC'
        )
        
        return {
          name: columnName,
          order: orderPart?.toUpperCase() === 'DESC' ? 'DESC' as const : 'ASC' as const
        }
      })
      .filter(col => col.name && col.name.length > 0)
  }
  
  return []
}

export function IndexDetailsView({ connection, database, indexName, schema, onIndexDeleted }: IndexDetailsViewProps) {
  const [indexDetails, setIndexDetails] = useState<{
    name?: string;
    index_name?: string;
    table?: string;
    tableName?: string;
    type?: string;
    unique?: boolean;
    primary?: boolean;
    cardinality?: number;
    columns?: IndexColumnWithOrder[];
    definition?: string;
    where?: string;
    comment?: string;
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const fetchIndexDetails = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await AgentProxyService.getIndexDefinition(
        connection.id,
        database,
        indexName,
        schema
      )
      
      // Handle the response which contains index definition and metadata
      if (response && typeof response === 'object') {
        const indexData: Record<string, unknown> = response !== null && !Array.isArray(response)
          ? (response as unknown as Record<string, unknown>)
          : {}
        
        // Debug: Log the API response to understand available fields
        console.log('Index details API response for', indexName, ':', indexData)
        
        const definition = typeof indexData.definition === 'string' ? indexData.definition : ''
        const parsedTableName = parseTableNameFromDefinition(definition)
        const parsedColumns = parseColumnsFromDefinition(definition)
        
        // Detect unique status from multiple possible sources
        const isUnique = Boolean(
          indexData.is_unique || 
          indexData.unique || 
          indexData.non_unique === false ||
          indexData.non_unique === 0 ||
          (definition && /\bUNIQUE\b/i.test(definition)) ||
          (indexName && /^(uk_|unique_|uidx_)/i.test(indexName))
        )
        
        // Detect primary key status
        const isPrimary = Boolean(
          indexData.is_primary || 
          indexData.primary || 
          (definition && /\bPRIMARY\s+KEY\b/i.test(definition)) ||
          (indexName && /^(pk_|primary_)/i.test(indexName))
        )

        setIndexDetails({
          name: indexName,
          index_name: ((): string | undefined => {
            const v = indexData.index_name
            return typeof v === 'string' ? v : undefined
          })(),
          definition: definition,
          table: parsedTableName || undefined,
          tableName: parsedTableName || undefined,
          type: ((): string => {
            const v = indexData.index_type
            return typeof v === 'string' && v ? v : 'BTREE'
          })(),
          unique: isUnique,
          primary: isPrimary,
          cardinality: ((): number | undefined => {
            const v = indexData.cardinality
            return typeof v === 'number' ? v : undefined
          })(),
          columns: parsedColumns,
          where: ((): string | undefined => {
            const v = indexData.where_clause
            return typeof v === 'string' ? v : undefined
          })(),
          comment: ((): string | undefined => {
            const v = indexData.comment
            return typeof v === 'string' ? v : undefined
          })()
        })
      } else {
        console.error('Failed to fetch index details: Invalid response format')
        showToast('error', 'Failed to load index details')
        setIndexDetails(null)
      }
    } catch (error) {
      console.error('Failed to fetch index details:', error)
      showToast('error', 'Failed to load index details')
      setIndexDetails(null)
    } finally {
      setIsLoading(false)
    }
  }, [connection.id, database, indexName, schema, showToast])

  useEffect(() => {
    void fetchIndexDetails()
  }, [fetchIndexDetails])

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await AgentProxyService.dropIndex(
        connection.id,
        database,
        indexName,
        schema
      )
      
      showToast('success', `Index "${indexName}" deleted successfully`)
      setShowDeleteConfirm(false)
      
      // Invalidate indexes query to refresh the list in parent components
      void queryClient.invalidateQueries({
        queryKey: [...databaseKeys.all, 'indexes', connection.id, database, schema]
      })
      
      // Call the parent callback to handle navigation/refresh
      onIndexDeleted?.()
    } catch (error: unknown) {
      console.error('Failed to delete index:', error)
      const message = error instanceof Error ? error.message : 'Failed to delete index'
      showToast('error', message)
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading index details...</p>
        </div>
      </div>
    )
  }

  if (!indexDetails) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <HashtagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Index Not Found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            The index "{indexName}" could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <HashtagIcon className="w-8 h-8 text-primary-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {indexDetails.name || indexDetails.index_name || indexName}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Index on {indexDetails.table || indexDetails.tableName}
                {schema && ` in schema ${schema}`}
              </p>
            </div>
          </div>
          
          <button type="button"
            onClick={() => { setShowDeleteConfirm(true) }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Drop Index
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Index Name
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1 font-mono">
                  {indexDetails.name || indexDetails.index_name}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Table
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {indexDetails.table || indexDetails.tableName}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Type
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {indexDetails.type || 'BTREE'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Unique
                </label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {indexDetails.unique ? 'Yes' : 'No'}
                </p>
              </div>

              {indexDetails.primary && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Primary Key
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    Yes
                  </p>
                </div>
              )}

              {indexDetails.cardinality !== undefined && (
                <div className="bg-white dark:bg-gray-700 p-3 rounded border">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Cardinality
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {indexDetails.cardinality ? indexDetails.cardinality.toLocaleString() : 'N/A'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Columns */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <TableCellsIcon className="w-5 h-5 mr-2" />
              Index Columns
            </h3>
            
            {indexDetails.columns && indexDetails.columns.length > 0 ? (
              <div className="space-y-2">
                {indexDetails.columns.map((column: IndexColumnWithOrder, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border"
                  >
                    <div className="flex items-center">
                      <span className="text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2 py-1 rounded mr-3">
                        {index + 1}
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white">
                        {column.name}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        column.order === 'DESC' 
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300' 
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                      }`}>
                        {column.order === 'DESC' ? 'DESC ↓' : 'ASC ↑'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">
                No column information available
              </p>
            )}
          </div>

          {/* Additional Properties */}
          {(indexDetails.definition || indexDetails.where || indexDetails.comment) && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 mr-2" />
                Additional Properties
              </h3>
              
              <div className="space-y-4">
                {indexDetails.definition && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Definition
                    </label>
                    <pre className="bg-white dark:bg-gray-700 p-3 rounded border text-sm font-mono text-gray-900 dark:text-white overflow-x-auto">
                      {indexDetails.definition}
                    </pre>
                  </div>
                )}

                {indexDetails.where && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      WHERE Clause (Partial Index)
                    </label>
                    <pre className="bg-white dark:bg-gray-700 p-3 rounded border text-sm font-mono text-gray-900 dark:text-white overflow-x-auto">
                      {indexDetails.where}
                    </pre>
                  </div>
                )}

                {indexDetails.comment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Comment
                    </label>
                    <p className="bg-white dark:bg-gray-700 p-3 rounded border text-sm text-gray-900 dark:text-white">
                      {indexDetails.comment}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Performance Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Index Performance Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Higher cardinality indexes are generally more selective and efficient</li>
                  <li>B-Tree indexes work well for equality and range queries</li>
                  <li>Unique indexes automatically enforce data integrity constraints</li>
                  <li>Partial indexes (with WHERE clauses) save space and improve performance</li>
                  <li>Consider the order of columns based on your query patterns</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Drop Index
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Are you sure you want to drop the index "{indexName}"? This action cannot be undone and may impact query performance.
                </p>
                <div className="flex justify-end gap-3">
                  <button type="button"
                    onClick={() => { setShowDeleteConfirm(false) }}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button type="button"
                    onClick={() => void handleDelete()}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDeleting ? 'Dropping...' : 'Drop Index'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
